// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngMiniCart from "@bg/App/Components/XngMiniCart/XngMiniCart";
import { xHeaderState } from "@bg/App/Store/Reducers";
import { checkoutPages } from "@bg/XDL/constants";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    canCheckoutWithCart,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps, DrawerProps } from "@insite/mobius/Drawer";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, Suspense, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {}

const mapStateToProps = (state: ApplicationState & xHeaderState) => {
    const cart = getCurrentCartState(state).value;
    const billToId = getSession(state).billToId;
    const currencyCode = getBillToState(state, billToId).value?.properties["currencyCode"];
    const pageType = getCurrentPage(state).type;
    const shouldLoadCart =
        !getCurrentCartState(state).value &&
        !getCurrentCartState(state).isLoading &&
        pageType !== "CheckoutReviewAndSubmitPage" &&
        pageType !== "CheckoutShippingPage" &&
        pageType !== "OnePageCheckoutPage" &&
        pageType !== "CartPage" &&
        pageType !== "RfqRequestQuotePage";

    return {
        cart,
        shouldLoadCart,
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        currencyCode,
        isAuthenticated: getSession(state).isAuthenticated,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        isCartCheckoutDisabled: isCartCheckoutDisabled(cart),
        isSticky: state.xHeader.isSticky,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        // XNG-Changes
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    setNavDrawerIsOpen,
};

export interface CartLinkStyles {
    cartTotalStyles?: TypographyPresentationProps;
    cartQuantityStyles?: TypographyPresentationProps;
    titleIcon?: IconPresentationProps;
    titleClickable?: ClickablePresentationProps;
    miniCartElementStyle?: InjectableCss;
    drawer?: DrawerPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    cartTotalStyles: {
        size: fontConfig.fontSize.xSmall,
        lineHeight: "16px",
        weight: fontConfig.weight.semiBold,
        color: "common.white",
        css: css``,
    },
    titleIcon: {
        css: css``,
    },
    titleClickable: {
        css: css`
            position: relative;
            &:hover {
                span {
                    color: ${getColor("text.link")};
                    svg {
                        fill: ${getColor("text.link")};
                    }
                }
            }
        `,
    },
    miniCartElementStyle: {
        css: css`
            &.shade {
                span {
                    color: ${getColor("common.white")};
                    text-decoration: underline;
                    font-weight: ${fontConfig.weight.bold};
                }
                svg {
                    fill: ${getColor("common.white")};
                }
                .title-clickable {
                    z-index: 20;
                }
            }
        `,
    },
    cartQuantityStyles: {
        size: 11,
        lineHeight: "13px",
        color: "common.white",
        css: css`
            letter-spacing: 0.51px;
        `,
    },
    drawer: {
        cssOverrides: {
            drawerContent: css`
                height: 100%;
            `,
            drawerBody: css`
                width: 520px;
            `,
        },
    },
};

interface CartTotalProps {
    cartTotal: string | undefined;
    isSticky: boolean;
    retainedTotalCountToDisplay: any;
}

const CartTotal = ({ cartTotal, isSticky, retainedTotalCountToDisplay }: CartTotalProps) => {
    return (
        <>
            {!isSticky && (
                <StyledWrapper
                    css={css`
                        min-height: 15px;
                        margin-top: 3px;
                    `}
                >
                    <Typography as="span" {...cartLinkStyles.cartTotalStyles} className={isSticky ? "sticky" : ""}>
                        {cartTotal}
                    </Typography>
                </StyledWrapper>
            )}
        </>
    );
};

interface OwnProps extends WidgetProps {
    fields: {};
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const styles = cartLinkStyles;
const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    currencyCode,
    isSticky,
    cart,
    orderSubTotalDisplay,
    isAuthenticated,
    loadCurrentCart,
    setNavDrawerIsOpen,
    navDrawerIsOpen,
    // XNG-Changes
    isCheckoutPage,
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const [isOpen, setIsOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(false);

    React.useEffect(() => {
        const updateSmallScreen = () => {
            setIsSmallScreen(window.matchMedia("(max-width: 768px) and (hover: none)").matches);
        };
        updateSmallScreen();
        window.addEventListener("resize", updateSmallScreen, { passive: true });
        return () => {
            window.removeEventListener("resize", updateSmallScreen);
        };
    }, []);

    const handleCloseModal = () => {
        setTimeout(() => {
            setIsOpen(false);
        }, 300);
    };

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const { cartTotalStyles } = styles;
    const cartSubTotal = orderSubTotalDisplay;

    const cartElement = () => (
        <StyledWrapper
            css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                position: relative;

                .qty-wrpr {
                    position: absolute;
                    top: -2px;
                    right: -1px;
                    min-width: 15px;
                    height: 16px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 0 2px;
                    border-radius: 4px;

                    &.no-sticky {
                        right: 28%;
                        top: -3px;
                        border-radius: 4px;
                        height: 15px;
                    }
                }
            `}
        >
            <Icon
                {...styles.titleIcon}
                size={isCheckoutPage ? 32 : isSticky ? 30 : 24}
                src="Cart"
                color={isCheckoutPage ? "text.link" : "common.white"}
            />
            {retainedTotalCountToDisplay &&
            retainedTotalCountToDisplay !== undefined &&
            retainedTotalCountToDisplay > 0 ? (
                <StyledWrapper
                    className={isSticky || isCheckoutPage ? "qty-wrpr" : "no-sticky qty-wrpr"}
                    css={css`
                        padding: 0 1.5px;
                        background-color: ${getColor("text.main")};
                    `}
                >
                    <Typography as="span" {...cartLinkStyles.cartQuantityStyles} color="common.white">
                        {retainedTotalCountToDisplay}
                    </Typography>
                </StyledWrapper>
            ) : (
                <></>
            )}
            {/* XNG-Changes */}
            {!isCheckoutPage && (
                <StyledWrapper
                    css={css`
                        text-align: center;
                    `}
                >
                    <CartTotal
                        cartTotal={cartSubTotal}
                        retainedTotalCountToDisplay={retainedTotalCountToDisplay}
                        isSticky={isSticky}
                        {...cartTotalStyles}
                    ></CartTotal>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );

    return (
        <>
            <Hidden below="md">
                <Clickable
                    {...styles.titleClickable}
                    data-test-selector="cartLink"
                    onClick={() => {
                        setTimeout(() => {
                            if (navDrawerIsOpen && !isOpen) {
                                setNavDrawerIsOpen({ navDrawerIsOpen: false });
                            }
                            setIsOpen(!isOpen);
                        }, 300);
                    }}
                >
                    {cartElement()}
                </Clickable>
                {!isSmallScreen && (
                    <Drawer
                        draggable={false}
                        position="right"
                        {...styles.drawer}
                        isOpen={isOpen}
                        handleClose={handleCloseModal}
                        contentLabel="cart menu drawer"
                    >
                        <Suspense fallback={<LoadingOverlay loading={true} />}>
                            <XngMiniCart closeOnClick={handleCloseModal} />
                        </Suspense>
                    </Drawer>
                )}
            </Hidden>
            <Hidden above="sm">
                <Clickable {...styles.titleClickable} data-test-selector="cartLink" href={cartUrl}>
                    {cartElement()}
                </Clickable>
            </Hidden>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartLink),
    definition: {
        displayName: "Header Cart Link",
        icon: "cart-shopping",
        fieldDefinitions: [],
        group: "XNGAGE" as any,
    },
};

export default widgetModule;
